// 共通スタイルオブジェクト - モダンミニマル（オレンジ/ピンク系）
import { COLORS, SPACING, BORDER_RADIUS, SHADOWS, TRANSITIONS } from './constants';

// よく使われる共通プロパティ
export const commonProps = {
  whiteText: { color: COLORS.white },
  smallText: { fontSize: '0.75rem' },
  bodyText: { fontSize: '0.875rem' },
  mediumText: { fontSize: '1rem' },
  pointer: { cursor: 'pointer' },
  noMargin: { margin: 0 },
  fullWidth: { width: '100%' }
};

// ボタンスタイル
export const buttonStyles = {
  primary: {
    padding: `${SPACING.md} ${SPACING.lg}`,
    background: COLORS.primaryGradient,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '0.9375rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: TRANSITIONS.smooth,
    boxShadow: SHADOWS.button
  },
  secondary: {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.white,
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: BORDER_RADIUS.md,
    fontSize: '0.9375rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: TRANSITIONS.default
  },
  danger: {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.error,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: TRANSITIONS.default
  },
  success: {
    padding: `${SPACING.md} ${SPACING.lg}`,
    background: COLORS.successGradient,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: TRANSITIONS.default
  },
  twitter: {
    padding: SPACING.md,
    background: 'linear-gradient(135deg, #1d9bf0, #0ea5e9)',
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: TRANSITIONS.smooth,
    boxShadow: '0 4px 12px rgba(29, 155, 240, 0.3)'
  },
  threads: {
    padding: SPACING.md,
    background: 'linear-gradient(135deg, #000, #333)',
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: TRANSITIONS.smooth,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  },
  ghost: {
    padding: `${SPACING.sm} ${SPACING.md}`,
    backgroundColor: 'transparent',
    color: COLORS.gray600,
    border: 'none',
    borderRadius: BORDER_RADIUS.sm,
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: TRANSITIONS.default
  }
};

// カードスタイル
export const cardStyles = {
  base: {
    backgroundColor: COLORS.white,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.lg,
    boxShadow: SHADOWS.card,
    border: '1px solid rgba(0, 0, 0, 0.04)'
  },
  compact: {
    backgroundColor: COLORS.white,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.md,
    border: `1px solid ${COLORS.gray200}`
  },
  highlight: {
    background: COLORS.primaryGradientSoft,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.lg,
    border: `1px solid rgba(255, 107, 53, 0.1)`
  },
  hover: {
    transform: 'translateY(-2px)',
    boxShadow: SHADOWS.cardHover
  }
};

// テキストスタイル
export const textStyles = {
  heading: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: COLORS.gray900,
    marginBottom: SPACING.sm,
    lineHeight: 1.3
  },
  subheading: {
    fontSize: '1rem',
    fontWeight: '600',
    color: COLORS.gray800,
    marginBottom: SPACING.sm
  },
  body: {
    fontSize: '0.9375rem',
    color: COLORS.gray600,
    lineHeight: '1.6'
  },
  small: {
    fontSize: '0.8125rem',
    color: COLORS.gray500
  },
  error: {
    fontSize: '0.875rem',
    color: COLORS.error
  },
  success: {
    fontSize: '0.875rem',
    color: COLORS.success
  },
  primary: {
    fontSize: '0.875rem',
    color: COLORS.primary,
    fontWeight: '500'
  }
};

// フォーム要素スタイル
export const formStyles = {
  input: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: `2px solid ${COLORS.gray200}`,
    borderRadius: BORDER_RADIUS.md,
    fontSize: '1rem',
    transition: TRANSITIONS.default,
    backgroundColor: COLORS.white
  },
  inputFocus: {
    borderColor: COLORS.primary,
    boxShadow: '0 0 0 4px rgba(255, 107, 53, 0.1)',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    minHeight: '6rem',
    padding: '0.875rem 1rem',
    border: `2px solid ${COLORS.gray200}`,
    borderRadius: BORDER_RADIUS.md,
    fontSize: '1rem',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: TRANSITIONS.default,
    backgroundColor: COLORS.white
  },
  select: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: `2px solid ${COLORS.gray200}`,
    borderRadius: BORDER_RADIUS.md,
    fontSize: '1rem',
    backgroundColor: COLORS.white,
    transition: TRANSITIONS.default,
    cursor: 'pointer'
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: COLORS.gray800,
    marginBottom: SPACING.sm
  }
};

// レイアウトスタイル
export const layoutStyles = {
  container: {
    maxWidth: '28rem',
    margin: '0 auto',
    padding: SPACING.lg
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.md
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.md
  },
  grid: {
    display: 'grid',
    gap: SPACING.md
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
};

// バッジスタイル
export const badgeStyles = {
  free: {
    padding: '0.375rem 1rem',
    backgroundColor: COLORS.gray100,
    color: COLORS.gray600,
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600'
  },
  premium: {
    padding: '0.375rem 1rem',
    background: COLORS.premiumGradient,
    color: COLORS.white,
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)'
  },
  success: {
    padding: '0.375rem 1rem',
    backgroundColor: COLORS.successLight,
    color: COLORS.success,
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600'
  },
  info: {
    padding: '0.375rem 1rem',
    background: COLORS.primaryGradientSoft,
    color: COLORS.primary,
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600'
  }
};

// モーダルスタイル
export const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: COLORS.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: SPACING.lg
  },
  content: {
    backgroundColor: COLORS.white,
    borderRadius: BORDER_RADIUS.xl,
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
    boxShadow: SHADOWS.xl
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${SPACING.lg} ${SPACING.lg} ${SPACING.md}`,
    borderBottom: `1px solid ${COLORS.gray100}`
  },
  body: {
    padding: SPACING.lg
  },
  footer: {
    padding: `${SPACING.md} ${SPACING.lg} ${SPACING.lg}`,
    borderTop: `1px solid ${COLORS.gray100}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: SPACING.md
  }
};

// エラー・成功メッセージスタイル
export const messageStyles = {
  error: {
    padding: '1rem 1.25rem',
    backgroundColor: COLORS.errorLight,
    border: `1px solid #fecaca`,
    borderRadius: BORDER_RADIUS.md,
    color: COLORS.error,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem'
  },
  success: {
    padding: '1rem 1.25rem',
    backgroundColor: COLORS.successLight,
    border: `1px solid #a7f3d0`,
    borderRadius: BORDER_RADIUS.md,
    color: COLORS.success,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem'
  },
  info: {
    padding: '1rem 1.25rem',
    background: COLORS.primaryGradientSoft,
    border: `1px solid rgba(255, 107, 53, 0.2)`,
    borderRadius: BORDER_RADIUS.md,
    color: COLORS.primaryDark,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem'
  },
  warning: {
    padding: '1rem 1.25rem',
    backgroundColor: COLORS.warningLight,
    border: `1px solid #fde68a`,
    borderRadius: BORDER_RADIUS.md,
    color: '#92400e',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem'
  }
};

// 生成結果コンテナスタイル
export const generatedPostStyles = {
  container: {
    background: COLORS.primaryGradientSoft,
    border: `2px solid rgba(255, 107, 53, 0.15)`,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.lg,
    marginTop: SPACING.lg,
    position: 'relative'
  },
  label: {
    position: 'absolute',
    top: '-10px',
    left: '16px',
    background: COLORS.primaryGradient,
    color: COLORS.white,
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600'
  },
  content: {
    fontSize: '1rem',
    lineHeight: '1.7',
    color: COLORS.gray800,
    whiteSpace: 'pre-wrap'
  }
};

// 使用量バースタイル
export const usageBarStyles = {
  container: {
    height: '8px',
    backgroundColor: COLORS.gray200,
    borderRadius: '9999px',
    overflow: 'hidden'
  },
  fill: {
    height: '100%',
    background: COLORS.primaryGradient,
    borderRadius: '9999px',
    transition: 'width 0.3s ease'
  },
  fillWarning: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
  },
  fillCritical: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
  }
};
